import {
  ORDER_FEATURES,
  OrderFeature,
  PropertyData,
  TKV2_ORDER_ATTACHMENT_API,
  Tkv2OrderAttachment,
  ToolkitOrderID,
} from '@aloft/toolkit-shared';

import {
  fetchGetAllCompData,
  fetchGetSubjectData,
  fetchGetTkv2OrderFeaturesAPI,
  usePutTkv2OrderFeaturesAPI,
} from '../../../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../../../hooks/api/toolkitContext.ts';
import { loadOrderFeatures } from '../../../../../redux/features/orderSlice.ts';
import { useAppDispatch } from '../../../../../redux/hooks.ts';

const GET_ALL_COMP_DATA_API_SCHEMA = TKV2_ORDER_ATTACHMENT_API.COMP_DATA.GET.RESPONSE;
const GET_SUBJECT_DATA_API_SCHEMA = TKV2_ORDER_ATTACHMENT_API.SUBJECT_DATA.GET.RESPONSE;

export const useUpdateOrderFeatures = () => {
  const orderFeaturesSchema = TKV2_ORDER_ATTACHMENT_API.ORDER_FEATURES_API.GET.RESPONSE;

  const { fetcherOptions } = useToolkitContext();
  const { mutate: orderFeaturesMutate } = usePutTkv2OrderFeaturesAPI();
  const dispatch = useAppDispatch();

  return async (orderID: ToolkitOrderID, newProperties?: PropertyData[]) => {
    try {
      const subjectDataPromise = fetchGetSubjectData({
        ...fetcherOptions,
        pathParams: { orderID },
      });
      const compDataPromise = fetchGetAllCompData({
        ...fetcherOptions,
        pathParams: { orderID },
      });
      const orderFeaturesPromise = fetchGetTkv2OrderFeaturesAPI({
        ...fetcherOptions,
        pathParams: { orderID },
      });

      const [subjectDataResponse, compDataResponse, orderFeaturesResponse] = await Promise.all([
        subjectDataPromise,
        compDataPromise,
        orderFeaturesPromise,
      ]);

      const compData = GET_ALL_COMP_DATA_API_SCHEMA.parse(compDataResponse).comps;
      const subjectData = GET_SUBJECT_DATA_API_SCHEMA.parse(subjectDataResponse);
      const orderFeatures = orderFeaturesSchema.parse(
        orderFeaturesResponse as Tkv2OrderAttachment<'orderFeatures'>,
      ).body.features;

      const subjectImprovements = subjectData.editableData.improvements;
      const compsImprovements = Object.values(compData).map(
        (comp) => comp.editableData.improvements,
      );
      const newCompImprovements = (newProperties || []).map(
        (property) => property.editableData.improvements,
      );
      const improvements = [subjectImprovements, ...compsImprovements, ...newCompImprovements];

      const propertyFeatures = (Object.keys(ORDER_FEATURES) as OrderFeature[]).filter((feature) =>
        improvements.some((improvement) => ORDER_FEATURES[feature].hasFeature(improvement)),
      );

      const uniqueFeatures = Array.from(new Set(orderFeatures.concat(propertyFeatures)));

      dispatch(loadOrderFeatures(uniqueFeatures));

      return orderFeaturesMutate({
        pathParams: { orderID },
        body: TKV2_ORDER_ATTACHMENT_API.ORDER_FEATURES_API.PUT.REQUEST.BODY.parse({
          version: 1,
          features: uniqueFeatures,
        }),
      });
    } catch (e: unknown) {
      console.error('Failed to update order features', e);
      return undefined;
    }
  };
};
